import React from "react";
import { Helmet } from "react-helmet";
import { useLive } from "../LiveProvider";
import LiveHeader from "../common/LiveHeader";
import LiveRdcPoweredBy from "./LiveRdcPoweredBy";
import LiveRdcProgressBar from "./LiveRdcProgressBar";
import LiveRdcValueGrid from "./LiveRdcValueGrid";
import {
  Container,
  Description,
  PoweredByContainer,
  ProgressBarContainer,
  SubTitle,
  Title
} from "./styled/live-rdc";
import { Cell, Charts, Grid } from "./styled/live-rdc-cross-market-demand";

function friendlyList(array) {
  const newArray = [...array];

  if (newArray.length > 1) {
    const lastItem = newArray.pop();
    return `${newArray.join(", ")}${
      array.length > 2 ? "," : ""
    } and ${lastItem}`;
  }

  return newArray[0] || "";
}

function LiveRdcMarketDemand() {
  const { rdc } = useLive();
  const zipCodeViews = rdc.loading
    ? [{ label: "Loading...", value: 0 }]
    : rdc.data?.demands?.cityDemands?.map((demand) => ({
        label: [
          demand.consumerCity,
          [
            demand.consumerState,
            demand.consumerZip ? `(${demand.consumerZip})` : ""
          ]
            .filter(Boolean)
            .join(" ")
        ]
          .filter(Boolean)
          .join(", "),
        value: demand.percentage * 100
      })) || [];
  const cityViews = rdc.loading
    ? [{ label: "Loading...", value: 0 }]
    : rdc.data?.demands?.cityDemands?.map((demand) => ({
        label: [demand.consumerCity, demand.consumerState]
          .filter(Boolean)
          .join(", "),
        value: demand.percentage * 100
      })) || [];

  return (
    <>
      <Helmet>
        <title>Cross-Market Demand</title>
      </Helmet>
      <LiveHeader>Cross-Market Demand</LiveHeader>
      <Container large>
        <Title>Where is the buyer demand in your market coming from?</Title>
        <Description>
          Buyer demand is defined as those who are working with a known real
          estate agent and searching for active, for-sale properties on
          Realtor.com®.
        </Description>
        {rdc.loading ? (
          <Description style={{ height: "4.4rem" }}>Loading...</Description>
        ) : !!zipCodeViews.length && !!cityViews.length ? (
          <Description>
            Based on Realtor.com® search results, the bulk of buyer interest is
            coming from zip codes (
            {friendlyList(
              zipCodeViews
                .slice(0, 3)
                .map(({ label }) => `${label.replace(/\D+/g, "")}`)
            )}
            ) and (
            {friendlyList(cityViews.slice(0, 3).map(({ label }) => label))}).
          </Description>
        ) : (
          <Description>There are currently no results to show.</Description>
        )}
        <Charts>
          <Grid>
            <Cell>
              <SubTitle>Top zip codes by % of total views</SubTitle>
              <ProgressBarContainer>
                {zipCodeViews.map((view) => (
                  <LiveRdcProgressBar
                    key={view.label}
                    label={view.label}
                    value={view.value}
                  />
                ))}
                {!zipCodeViews.length && (
                  <LiveRdcProgressBar label="N/A" value={0} />
                )}
              </ProgressBarContainer>
            </Cell>
            <Cell>
              <SubTitle>Top cities by % of total views</SubTitle>
              <ProgressBarContainer>
                {cityViews.map((view) => (
                  <LiveRdcProgressBar
                    key={view.label}
                    label={view.label}
                    value={view.value}
                  />
                ))}
                {!cityViews.length && (
                  <LiveRdcProgressBar label="N/A" value={0} />
                )}
              </ProgressBarContainer>
            </Cell>
          </Grid>
        </Charts>
        <SubTitle>Why is this valuable?</SubTitle>
        <LiveRdcValueGrid
          items={[
            "If you are seeing a lot of distributed cross market demand, it can help influence your marketing strategy to target these buyers to gain more exposure for your home."
          ]}
        />
        <PoweredByContainer>
          <LiveRdcPoweredBy />
        </PoweredByContainer>
      </Container>
    </>
  );
}

export default LiveRdcMarketDemand;
